import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useModal } from '../../../components/Modal/useModal';
import { DEFAULT_ERROR } from '../../../components/Notifications/Notification';
import { postConfirmPayment } from '../../../core/api';
import { addNotification } from '../../../core/store/notificationsSlice';
import { SellerFilingReportToConfirm } from '../../../core/types';
import { useAppDispatch, useAppSelector } from '../../../hooks';

export default function usePaymentInstructionModal() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector((s) => s.user.user);
  const activeCompany = useAppSelector((s) => s.user.activeCompany);
  const { t } = useTranslation();
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

  const [reportToConfirmPayment, setSellerFilingReportToConfirmPayment] =
    useState<SellerFilingReportToConfirm | undefined>(undefined);

  const {
    isModalOpened: isPaymentInstructionModalOpened,
    closeModal: closePaymentInstructionModal,
    openModal: openPaymentInstructionModal,
  } = useModal();

  const onClickConfirmPayment =
    (report: SellerFilingReportToConfirm | undefined) => () => {
      if (!report) return;

      setIsConfirmingPayment(true);
      postConfirmPayment(
        report.payment_instruction.id,
        activeCompany?.company_id
      )
        .then((res) => {
          setIsConfirmingPayment(false);
          queryClient.setQueryData(
            report.completed_filing
              ? ['SellerCompletedFiling', user!.Id, activeCompany?.company_id]
              : ['SellerOngoingFiling', user!.Id, activeCompany?.company_id],
            (oldReports: SellerFilingReportToConfirm[] | undefined) => {
              return (oldReports || []).map((r) => {
                if (
                  r.id === report.id &&
                  r.payment_instruction.id === report.payment_instruction.id
                ) {
                  return {
                    ...r,
                    payment_instruction: {
                      ...r.payment_instruction,
                      confirmed: res,
                    },
                  };
                }

                return r;
              });
            }
          );
          dispatch(
            addNotification({
              text: t('*country* marked as paid').replace(
                '*country*',
                t(report.country.short_name)
              ),
              type: 'success',
            })
          );
          closePaymentInstructionModal();
        })
        .catch(() => {
          setIsConfirmingPayment(false);
          dispatch(
            addNotification({
              text: t(DEFAULT_ERROR),
              type: 'error',
            })
          );
        });
    };

  const handleOpenPaymentInstructionModal =
    (r: SellerFilingReportToConfirm) => () => {
      setSellerFilingReportToConfirmPayment(r);
      openPaymentInstructionModal();
    };

  return {
    onClickConfirmPayment,
    isPaymentInstructionModalOpened,
    closePaymentInstructionModal,
    reportToConfirmPayment,
    handleOpenPaymentInstructionModal,
    isConfirmingPayment,
  };
}
